"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryItems = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/wedding_ceremony_kiss.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/wedding_ceremony_kiss.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/wedding_ceremony_kiss.png-wvc-sizes",
  alt: "Anthony and Lily sharing their first kiss as a married couple",
  caption: "THE BIG DAY • 2024",
  containerClass: "md:col-span-8 md:col-start-1 md:row-start-1 md:row-span-3 relative",
  imgClass: "w-full h-[500px] md:h-[800px] object-cover rounded-md shadow-md",
  captionClass: "absolute bottom-6 left-6 text-xs tracking-widest font-default bg-background/80 backdrop-blur-sm px-4 py-2 rounded-sm text-foreground uppercase border border-border/30 shadow-sm"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/engagement_shoot_paris.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/engagement_shoot_paris.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/engagement_shoot_paris.png-wvc-sizes",
  alt: "Anthony and Lily during their engagement shoot in Paris",
  caption: "ENGAGEMENT • 2023",
  containerClass: "md:col-span-5 md:col-start-7 md:row-start-2 md:row-span-2 z-10 md:-ml-16 md:mt-32 relative mt-8 md:mt-0",
  imgClass: "w-full h-[350px] md:h-[500px] object-cover rounded-md shadow-lg border-8 border-background",
  captionClass: "absolute top-6 right-6 text-xs tracking-widest font-default bg-background/80 backdrop-blur-sm px-4 py-2 rounded-sm text-foreground uppercase border border-border/30 shadow-sm"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/family_dinner_celebration.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/family_dinner_celebration.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/family_dinner_celebration.png-wvc-sizes",
  alt: "A candid moment with family during the rehearsal dinner",
  caption: "FAMILY • 2024",
  containerClass: "md:col-span-5 md:col-start-2 md:row-start-4 md:row-span-2 z-0 md:-mt-24 relative mt-8 md:mt-0",
  imgClass: "w-full h-[300px] md:h-[450px] object-cover rounded-md shadow-md",
  captionClass: "absolute bottom-6 right-6 text-xs tracking-widest font-default bg-background/80 backdrop-blur-sm px-4 py-2 rounded-sm text-foreground uppercase border border-border/30 shadow-sm"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/travel_adventures_italy.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/travel_adventures_italy.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680521/img/travel_adventures_italy.png-wvc-sizes",
  alt: "Anthony and Lily exploring the Amalfi Coast",
  caption: "TRAVELS • 2022",
  containerClass: "md:col-span-6 md:col-start-7 md:row-start-4 md:row-span-3 z-10 md:-mt-12 relative mt-8 md:mt-0",
  imgClass: "w-full h-[450px] md:h-[700px] object-cover rounded-md shadow-lg",
  captionClass: "absolute top-1/2 -left-6 -translate-y-1/2 -rotate-90 origin-left text-xs tracking-widest font-default bg-background/80 backdrop-blur-sm px-4 py-2 rounded-sm text-foreground uppercase border border-border/30 shadow-sm whitespace-nowrap hidden md:block"
}];
function GalleryItem({
  item,
  index
}: {
  item: typeof galleryItems[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className={cn(item.containerClass, "transition-all duration-1000 ease-out group", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")}>
      <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} className={cn(item.imgClass, "transition-transform duration-700 group-hover:scale-[1.02]")} loading="lazy" />
      <span className={item.captionClass}>{item.caption}</span>
    </div>;
}
export default function SolidWoodDoor() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section data-section-id="262073" id="captured-moments" className="bg-background text-foreground py-24 md:py-32 px-4 md:px-8 lg:px-12 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-accent/20 via-background to-background pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div ref={headerRef} className={cn("max-w-3xl mb-16 md:mb-24 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <h2 className="font-default text-4xl md:text-6xl lg:text-7xl font-bold tracking-tighter uppercase mb-6 text-foreground">
            Captured Moments
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-serif leading-relaxed max-w-2xl mb-8">
            A visual journey through our favorite memories—from the quiet moments of our engagement to the grand adventures that brought us here today.
          </p>
          <Button asChild variant="outline" size="lg" className="rounded-md border-border/50 hover:bg-accent transition-colors duration-300">
            <Link to="/GalleryPage">
              View Full Gallery
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 md:grid-rows-[auto_auto_auto_auto_auto] gap-y-8 md:gap-y-0">
          {galleryItems.map((item, index) => <GalleryItem key={item.id} item={item} index={index} />)}
        </div>
      </div>
    </section>;
}